import { useQuery } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";
import React, { useState } from "react";
import styled from "@emotion/styled";
import Image from "@components/item/Image";
import BasicDrawer from "@components/drawer/BasicDrawer";
import { useTheme } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import BackHeader from "@components/header/BackHeader";
import { useTranslation } from "react-i18next";

const SalesList = () => {
  const { getSalesList, getSalesSortList } = useApiRequest();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const theme = useTheme();
  const navigate = useNavigate();
  const language = localStorage.getItem("language");
  const { t } = useTranslation();

  const { data: salesSortList } = useQuery({
    queryKey: ["salesSortList"],
    queryFn: () => getSalesSortList(),
  });

  const { data: salesList } = useQuery({
    queryKey: ["salesList", selectedStatus],
    queryFn: () => getSalesList(selectedStatus),
  });

  return (
    <Container listLength={salesList?.length || 0}>
      <BackHeader title="판매내역" />
      {/* 판매 상태 Button */}
      <SortButton onClick={() => setIsOpen(true)}>
        <span>
          {salesSortList?.find((item: any) => item.code === selectedStatus)
            ? salesSortList?.find((item: any) => item.code === selectedStatus)
                .name_ko
            : t("전체")}
        </span>
        <Image
          src={theme.image.arrow_down.default}
          width="1.6rem"
          height="1.6rem"
        />
      </SortButton>

      {/* 판매 상태 Drawer */}
      <BasicDrawer open={isOpen} setOpen={setIsOpen}>
        <SortList>
          <SortItem
            isSelected={selectedStatus === ""}
            onClick={() => setSelectedStatus("")}>
            <span>{t("전체")}</span>
            {selectedStatus === "" && (
              <Image
                src={theme.image.check_green.default}
                width="2rem"
                height="2rem"
              />
            )}
          </SortItem>

          {/* 판매 상태 리스트 */}
          {salesSortList?.map((sort: any) => (
            <SortItem
              key={sort.code}
              isSelected={selectedStatus === sort.code}
              onClick={() => setSelectedStatus(sort.code)}>
              <span>{language === "ko" ? sort.name_ko : sort.name_en}</span>
              {selectedStatus === sort.code && (
                <Image
                  src={theme.image.check_green.default}
                  width="2rem"
                  height="2rem"
                />
              )}
            </SortItem>
          ))}
        </SortList>
      </BasicDrawer>

      {/* 판매 내역 리스트 */}
      {salesList?.map((sale: any) => (
        <OrderItemContainer
          key={sale.order_id}
          onClick={() => navigate(`/sales-detail/${sale.order_id}`)}>
          <OrderHeader>
            <OrderDate>{sale.created_at}</OrderDate>
            <Image
              src={theme.image.arrow_right.default}
              width="2rem"
              height="2rem"
            />
          </OrderHeader>

          {/* 판매자 정보 */}
          <SellerName>{sale.user_name}</SellerName>

          <ProductInfo>
            <ImageContainer>
              <ImageWrapper>
                <Image src={sale.product_image} width="6rem" height="6rem" />
              </ImageWrapper>

              {sale.total_product_count > 1 && (
                <QuantityText>외 {sale.total_product_count - 1}개</QuantityText>
              )}
            </ImageContainer>
            <ProductDetails>
              <BrandName>
                {language === "en" && sale?.brand_name_en
                  ? sale.brand_name_en
                  : sale.brand_name_ko}
              </BrandName>

              <ProductName>
                {language === "en" && sale?.product_name_en
                  ? sale.product_name_en
                  : sale.product_name_ko}
              </ProductName>

              <OptionText>
                {t("옵션")} : {sale.color} / {sale.size}
              </OptionText>

              <StatusBadge>
                {language === "ko"
                  ? sale.order_status_name_ko
                  : sale.order_status_name_en}
              </StatusBadge>
            </ProductDetails>
          </ProductInfo>
        </OrderItemContainer>
      ))}
    </Container>
  );
};

export default SalesList;

const Container = styled.div<{ listLength: number }>(
  ({ theme, listLength }) => ({
    minHeight: "100vh",
    backgroundColor:
      listLength > 1 ? theme.color.border_gray : theme.color.white,
  }),
);

const SortButton = styled.button(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "0.4rem",
  fontSize: "1.4rem",
  cursor: "pointer",
  backgroundColor: theme.color.white,
  width: "100%",
  padding: "2rem 2rem 1.2rem 2rem",
}));

const SortList = styled.div({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

const SortItem = styled.button<{ isSelected: boolean }>(
  ({ theme, isSelected }) => ({
    width: "100%",
    color: isSelected ? theme.color.green : theme.color.black,
    backgroundColor: "transparent",
    borderBottom: `1px solid ${theme.color.border_gray}`,
    cursor: "pointer",
    minHeight: "5.6rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "&:last-child": {
      border: "none",
    },
  }),
);

const OrderItemContainer = styled.div(({ theme }) => ({
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  padding: "2rem",
  backgroundColor: theme.color.white,
  marginBottom: "0.7rem",
}));

const OrderHeader = styled.div(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  borderBottom: `1px solid ${theme.color.border_gray}`,
  paddingBottom: "2rem",
}));

const OrderDate = styled.span(({ theme }) => ({
  fontSize: "1.4rem",
  fontWeight: theme.fontWeight.bold,
}));

const SellerName = styled.div(({ theme }) => ({
  fontWeight: theme.fontWeight.bold,
  margin: "1rem 0",
}));

const StatusBadge = styled.div(({ theme }) => ({
  fontSize: "1.2rem",
  fontWeight: theme.fontWeight.bold,
  textAlign: "end",
}));

const ProductInfo = styled.div({
  display: "flex",
  gap: "1rem",
});

const ImageWrapper = styled.div({
  height: "6rem",
});

const ImageContainer = styled.div({
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
});

const ProductDetails = styled.div({
  display: "flex",
  flexDirection: "column",
  flex: 1,
});

const BrandName = styled.div(({ theme }) => ({
  fontSize: "1.4rem",
  fontWeight: theme.fontWeight.bold,
}));

const ProductName = styled.div(({ theme }) => ({
  fontSize: "1.2rem",
  color: theme.color.gray,
}));

const OptionText = styled.div(({ theme }) => ({
  marginTop: "1.7rem",
  fontSize: "1.2rem",
  color: theme.color.gray,
}));

const QuantityText = styled.div(({ theme }) => ({
  fontSize: "1.2rem",
  color: theme.color.gray,
}));
